class DrawerSearchSection extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;

    this.drawerClass = "wt-drawer-search";
    this.drawer = document.querySelector(`.${this.drawerClass}`);
    this.classDrawerActive = `${this.drawerClass}--active`;

    this.pageOverlayClass = "search-overlay";
    this.activeOverlayBodyClass = `${this.pageOverlayClass}-on`;

    this.body = document.body;

    this.triggerQuery = [
      ".wt-header__search-trigger",
      ".wt-header__search__close",
    ].join(", ");

    this.triggers = () => document.querySelectorAll(this.triggerQuery);

    /*
     * Search state.
     */
    this.cachedResults = {};
    this.searchAbortController = null;
    this.currentRequestId = 0;

    this.input = this.querySelector('input[name="q"]');

    this.clearButton = this.querySelector(
      ".wt-header__search__clear-button",
    );

    this.closeButton = this.querySelector(
      ".wt-header__search__close",
    );

    this.predictiveSearchResults = this.querySelector(
      "[data-predictive-search]",
    );

    this.mainTrigger = this.querySelector(
      ".wt-header__search-trigger",
    );

    this.emptyAnnouncement = this.querySelector(
      ".search-empty",
    );

    this.statusElement = null;
    this.loadingText = null;
    this.liveRegionTimeout = null;

    this.isVisibleClearButton = false;

    /*
     * Bind methods used as event handlers.
     */
    this.saveSuggestionMenuInDesignMode =
      this.saveSuggestionMenuInDesignMode.bind(this);

    this.onFormSubmit =
      this.onFormSubmit.bind(this);

    this.onKeydown =
      this.onKeydown.bind(this);

    this.onInputStateChange =
      this.onInputStateChange.bind(this);

    this.onClearButtonClick =
      this.onClearButtonClick.bind(this);

    this.onClearButtonKeydown =
      this.onClearButtonKeydown.bind(this);

    this.onTriggerClick =
      this.onTriggerClick.bind(this);

    this.debouncedOnChange = debounce(() => {
      this.onChange();
    }, 300);

    this.toggleTabindexElements = [
      this.input,
      this.closeButton,
    ].filter(Boolean);

    this.setupEventListeners();
    this.init();
  }

  /*
   * Drawer focus management.
   */
  getFocusableElements() {
    const focusableElementsSelector = [
      "button",
      "[href]",
      "input:not([type='hidden'])",
      "select",
      "textarea",
      "[tabindex]",
    ].join(", ");

    const elements = Array.from(
      this.querySelectorAll(focusableElementsSelector),
    ).filter((element) => {
      return (
        !element.hasAttribute("disabled") &&
        element.getAttribute("aria-hidden") !== "true" &&
        element.tabIndex >= 0 &&
        element.offsetParent !== null
      );
    });

    return {
      elements,
      first: elements[0] || null,
      last: elements[elements.length - 1] || null,
    };
  }

  onToggle() {
    if (!this.drawer || !this.input) {
      return;
    }

    const isCurrentlyOpen = this.hasAttribute("open");

    if (isCurrentlyOpen) {
      this.removeAttribute("open");

      setTabindex(
        this.toggleTabindexElements,
        "-1",
      );

      if (this.mainTrigger) {
        setTabindex(
          [this.mainTrigger],
          "0",
        );
      }

      this.isOpen = false;

      setTimeout(() => {
        this.mainTrigger?.focus();
      }, 0);

      return;
    }

    this.setAttribute("open", "");

    setTabindex(
      this.toggleTabindexElements,
      "0",
    );

    if (this.mainTrigger) {
      setTabindex(
        [this.mainTrigger],
        "-1",
      );
    }

    this.input.focus();
    this.isOpen = true;

    const hasQuery = this.getQuery().length > 0;

    this.drawer.classList.toggle(
      "is-empty",
      !hasQuery,
    );
  }

  toggleDrawerClasses() {
    if (!this.drawer) {
      return;
    }

    this.onToggle();

    this.drawer.classList.toggle(
      this.classDrawerActive,
    );

    this.body.classList.toggle(
      this.activeOverlayBodyClass,
    );
  }

  /*
   * Initial event handling.
   */
  init() {
    this.triggers().forEach((trigger) => {
      trigger.addEventListener(
        "click",
        this.onTriggerClick,
      );
    });

    if (this.clearButton) {
      this.clearButton.addEventListener(
        "click",
        this.onClearButtonClick,
      );

      this.clearButton.addEventListener(
        "keydown",
        this.onClearButtonKeydown,
      );
    }

    this.addEventListener(
      "keydown",
      this.onKeydown,
    );

    if (this.input) {
      this.input.addEventListener(
        "input",
        this.onInputStateChange,
      );

      this.updateClearButtonState();
    }
  }

  setupEventListeners() {
    const form = this.querySelector(
      "form.store-search-form",
    );

    if (form) {
      form.addEventListener(
        "submit",
        this.onFormSubmit,
      );
    }

    if (this.input) {
      this.input.addEventListener(
        "input",
        this.debouncedOnChange,
      );
    }

    if (
      typeof Shopify !== "undefined" &&
      Shopify.designMode
    ) {
      document.addEventListener(
        "shopify:section:load",
        this.saveSuggestionMenuInDesignMode,
      );
    }
  }

  onTriggerClick(event) {
    event.preventDefault();
    this.toggleDrawerClasses();
  }

  onClearButtonClick(event) {
    event.preventDefault();

    this.clearResults({
      clearInput: true,
      focusInput: true,
    });
  }

  onClearButtonKeydown(event) {
    if (
      event.key !== "Enter" &&
      event.key !== " "
    ) {
      return;
    }

    event.preventDefault();

    this.clearResults({
      clearInput: true,
      focusInput: true,
    });
  }

  onKeydown(event) {
    const isEscapePressed =
      event.key === "Escape" ||
      event.keyCode === 27 ||
      event.code === "Escape";

    if (
      isEscapePressed &&
      this.isOpen
    ) {
      event.preventDefault();
      this.toggleDrawerClasses();
      return;
    }

    const isTabPressed =
      event.key === "Tab" ||
      event.keyCode === 9 ||
      event.code === "Tab";

    if (
      !isTabPressed ||
      !this.isOpen
    ) {
      return;
    }

    const {
      first,
      last,
    } = this.getFocusableElements();

    if (!first || !last) {
      return;
    }

    if (
      event.shiftKey &&
      document.activeElement === first
    ) {
      last.focus();
      event.preventDefault();
      return;
    }

    if (
      !event.shiftKey &&
      document.activeElement === last
    ) {
      first.focus();
      event.preventDefault();
    }
  }

  onInputStateChange() {
    this.updateClearButtonState();

    const hasQuery = this.getQuery().length > 0;

    if (this.drawer) {
      this.drawer.classList.toggle(
        "is-empty",
        !hasQuery,
      );
    }
  }

  updateClearButtonState() {
    if (
      !this.input ||
      !this.clearButton
    ) {
      return;
    }

    const shouldShow =
      this.input.value.length > 0;

    this.clearButton.style.display =
      shouldShow ? "flex" : "none";

    this.clearButton.setAttribute(
      "tabindex",
      shouldShow ? "0" : "-1",
    );

    this.isVisibleClearButton =
      shouldShow;
  }

  saveSuggestionMenuInDesignMode() {
    if (
      this.drawer &&
      this.body.classList.contains(
        this.activeOverlayBodyClass,
      )
    ) {
      this.drawer.classList.toggle(
        this.classDrawerActive,
      );
    }
  }

  /*
   * Search form handling.
   */
  getQuery() {
    return this.input?.value.trim() || "";
  }

  onChange() {
    const searchTerm = this.getQuery();

    if (searchTerm.length > 0) {
      this.getSearchResults(searchTerm);
      return;
    }

    this.clearResults();
  }

  onFormSubmit(event) {
    const searchTerm = this.getQuery();

    if (!searchTerm.length) {
      event.preventDefault();
      return;
    }

    const selectedResultLink =
      this.querySelector(
        '[aria-selected="true"] a',
      );

    if (selectedResultLink) {
      event.preventDefault();
      selectedResultLink.click();
    }
  }

  /*
   * Hybrid search process.
   *
   * 1. Request predictive search.
   * 2. Display predictive results if products are found.
   * 3. If predictive search finds no products, request normal search.
   */
  async getSearchResults(searchTerm) {
    if (
      !this.predictiveSearchResults ||
      !this.drawer
    ) {
      return;
    }

    const normalisedTerm =
      searchTerm.trim();

    if (!normalisedTerm.length) {
      this.clearResults();
      return;
    }

    const queryKey = `hybrid-${normalisedTerm
      .toLowerCase()
      .replace(/\s+/g, "-")}`;

    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(
        this.cachedResults[queryKey],
      );

      return;
    }

    /*
     * Cancel the previous search request.
     */
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }

    this.searchAbortController =
      new AbortController();

    const signal =
      this.searchAbortController.signal;

    /*
     * Each search receives an ID so that an older response cannot
     * replace results belonging to a newer search.
     */
    const requestId =
      ++this.currentRequestId;

    try {
      let resultsMarkup = "";

      /*
       * Try predictive search first.
       */
      try {
        const predictiveMarkup =
          await this.fetchPredictiveResults(
            normalisedTerm,
            signal,
          );

        if (
          signal.aborted ||
          requestId !== this.currentRequestId ||
          normalisedTerm !== this.getQuery()
        ) {
          return;
        }

        if (
          this.markupContainsProducts(
            predictiveMarkup,
          )
        ) {
          resultsMarkup =
            predictiveMarkup;
        }
      } catch (predictiveError) {
        if (
          predictiveError.name === "AbortError"
        ) {
          return;
        }

        /*
         * A predictive-search error should not prevent the normal
         * search fallback from running.
         */
        console.warn(
          "Predictive search failed. Trying normal search.",
          predictiveError,
        );
      }

      /*
       * Predictive search returned no products, or failed.
       * Use Shopify's normal storefront search.
       */
      if (!resultsMarkup) {
        resultsMarkup =
          await this.fetchStorefrontResults(
            normalisedTerm,
            signal,
          );
      }

      if (
        signal.aborted ||
        requestId !== this.currentRequestId ||
        normalisedTerm !== this.getQuery()
      ) {
        return;
      }

      if (!resultsMarkup) {
        this.renderNoResults();
        return;
      }

      this.cachedResults[queryKey] =
        resultsMarkup;

      this.renderSearchResults(
        resultsMarkup,
      );
    } catch (error) {
      if (error.name === "AbortError") {
        return;
      }

      console.error(
        "Search drawer request failed:",
        error,
      );

      this.renderSearchError();
    } finally {
      if (
        requestId === this.currentRequestId &&
        this.searchAbortController &&
        !this.searchAbortController.signal.aborted
      ) {
        this.searchAbortController = null;
      }
    }
  }

  /*
   * Shopify predictive search request.
   */
  async fetchPredictiveResults(
    searchTerm,
    signal,
  ) {
    const predictiveSearchUrl =
      window.routes?.predictive_search_url ||
      "/search/suggest";

    const parameters =
      new URLSearchParams({
        q: searchTerm,
        "resources[type]":
          "product,page,article,collection,query",
        "resources[limit_scope]":
          "each",
        "resources[limit]":
          "6",
        "resources[options][fields]":
          "title,variants.sku,product_type",
        section_id:
          "predictive-search",
      });

    const response = await fetch(
      `${predictiveSearchUrl}?${parameters.toString()}`,
      {
        signal,
        headers: {
          "X-Requested-With":
            "XMLHttpRequest",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Predictive search failed with status ${response.status}`,
      );
    }

    const responseText =
      await response.text();

    return this.extractSearchSectionMarkup(
      responseText,
    );
  }

  /*
   * Normal Shopify storefront search request.
   *
   * These parameters match the full product search page.
   */
  async fetchStorefrontResults(
    searchTerm,
    signal,
  ) {
    const shopRoot =
      window.Shopify?.routes?.root ||
      "/";

    const parameters =
      new URLSearchParams({
        q: searchTerm,
        type: "product",
        "options[prefix]": "last",
        "options[unavailable_products]":
          "last",
        section_id:
          "predictive-search",
      });

    const response = await fetch(
      `${shopRoot}search?${parameters.toString()}`,
      {
        signal,
        headers: {
          "X-Requested-With":
            "XMLHttpRequest",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Storefront search failed with status ${response.status}`,
      );
    }

    const responseText =
      await response.text();

    return this.extractSearchSectionMarkup(
      responseText,
    );
  }

  /*
   * Extract the predictive-search section from Shopify's
   * section-rendering response.
   */
  extractSearchSectionMarkup(
    responseText,
  ) {
    const parsedDocument =
      new DOMParser().parseFromString(
        responseText,
        "text/html",
      );

    const section =
      parsedDocument.querySelector(
        "#shopify-section-predictive-search",
      );

    if (!section) {
      throw new Error(
        "The predictive-search section was not found in the response.",
      );
    }

    return section.innerHTML.trim();
  }

  /*
   * The Wonder predictive-search Liquid renders each product as:
   *
   * .search-result-list__item
   */
  markupContainsProducts(
    resultsMarkup,
  ) {
    if (!resultsMarkup) {
      return false;
    }

    const parsedDocument =
      new DOMParser().parseFromString(
        resultsMarkup,
        "text/html",
      );

    return (
      parsedDocument.querySelector(
        ".search-result-list__item",
      ) !== null
    );
  }

  /*
   * Live-region accessibility handling.
   */
  setLiveRegionLoadingState() {
    this.statusElement =
      this.statusElement ||
      this.querySelector(
        ".predictive-search-status",
      );

    this.loadingText =
      this.loadingText ||
      this.getAttribute(
        "data-loading-text",
      ) ||
      "Loading search results";

    if (this.statusElement) {
      this.setLiveRegionText(
        this.loadingText,
      );
    }

    this.setAttribute(
      "loading",
      "",
    );
  }

  setLiveRegionText(statusText) {
    if (
      !this.statusElement ||
      !statusText
    ) {
      return;
    }

    clearTimeout(
      this.liveRegionTimeout,
    );

    this.statusElement.setAttribute(
      "aria-hidden",
      "false",
    );

    this.statusElement.textContent =
      statusText.trim();

    this.liveRegionTimeout =
      setTimeout(() => {
        if (this.statusElement) {
          this.statusElement.setAttribute(
            "aria-hidden",
            "true",
          );
        }
      }, 1000);
  }

  setLiveRegionResults() {
    this.removeAttribute(
      "loading",
    );

    const countElement =
      this.querySelector(
        "[data-predictive-search-live-region-count-value]",
      );

    const countText =
      countElement?.textContent?.trim();

    if (countText) {
      this.setLiveRegionText(
        countText,
      );
    }
  }

  /*
   * Results rendering.
   */
  renderSearchResults(
    resultsMarkup,
  ) {
    if (
      !this.predictiveSearchResults ||
      !this.drawer
    ) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      resultsMarkup;

    const hasProductResults =
      this.predictiveSearchResults.querySelector(
        ".search-result-list__item",
      ) !== null;

    this.toggleAttribute(
      "results",
      hasProductResults,
    );

    this.toggleAttribute(
      "empty",
      !hasProductResults,
    );

    /*
     * The query is not blank. Removing the initial empty state
     * allows either products or the no-results message to display.
     */
    this.drawer.classList.remove(
      "is-empty",
    );

    this.setLiveRegionResults();
  }

  renderNoResults() {
    if (
      !this.predictiveSearchResults ||
      !this.drawer
    ) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      "";

    this.removeAttribute(
      "loading",
    );

    this.removeAttribute(
      "results",
    );

    this.setAttribute(
      "empty",
      "",
    );

    this.drawer.classList.remove(
      "is-empty",
    );

    if (this.statusElement) {
      this.setLiveRegionText(
        "No search results found.",
      );
    }
  }

  renderSearchError() {
    if (
      !this.predictiveSearchResults ||
      !this.drawer
    ) {
      return;
    }

    this.predictiveSearchResults.innerHTML =
      "";

    this.removeAttribute(
      "loading",
    );

    this.removeAttribute(
      "results",
    );

    this.setAttribute(
      "empty",
      "",
    );

    this.drawer.classList.remove(
      "is-empty",
    );

    if (this.statusElement) {
      this.setLiveRegionText(
        "Search results could not be loaded.",
      );
    }
  }

  /*
   * Reset the drawer results.
   */
  clearResults({
    clearInput = false,
    focusInput = false,
  } = {}) {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
      this.searchAbortController = null;
    }

    /*
     * Invalidate any response belonging to the previous query.
     */
    this.currentRequestId += 1;

    if (
      clearInput &&
      this.input
    ) {
      this.input.value = "";
    }

    this.removeAttribute(
      "loading",
    );

    this.removeAttribute(
      "results",
    );

    this.setAttribute(
      "empty",
      "",
    );

    if (this.drawer) {
      this.drawer.classList.add(
        "is-empty",
      );
    }

    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.innerHTML =
        "";
    }

    this.updateClearButtonState();

    if (focusInput) {
      this.input?.focus();
    }
  }

  /*
   * Remove listeners if Shopify reloads the section.
   */
  disconnectedCallback() {
    if (this.searchAbortController) {
      this.searchAbortController.abort();
    }

    this.currentRequestId += 1;

    clearTimeout(
      this.liveRegionTimeout,
    );

    this.triggers().forEach((trigger) => {
      trigger.removeEventListener(
        "click",
        this.onTriggerClick,
      );
    });

    this.clearButton?.removeEventListener(
      "click",
      this.onClearButtonClick,
    );

    this.clearButton?.removeEventListener(
      "keydown",
      this.onClearButtonKeydown,
    );

    this.removeEventListener(
      "keydown",
      this.onKeydown,
    );

    this.input?.removeEventListener(
      "input",
      this.onInputStateChange,
    );

    this.input?.removeEventListener(
      "input",
      this.debouncedOnChange,
    );

    const form = this.querySelector(
      "form.store-search-form",
    );

    form?.removeEventListener(
      "submit",
      this.onFormSubmit,
    );

    document.removeEventListener(
      "shopify:section:load",
      this.saveSuggestionMenuInDesignMode,
    );
  }
}

if (
  !customElements.get(
    "search-drawer",
  )
) {
  customElements.define(
    "search-drawer",
    DrawerSearchSection,
  );
}